import { createRoadmap, makeMember, makePhase, makeProject, STATUSES, SLOTS_PER_SPRINT, MAX_SPRINT_COUNT, resolveOverlaps, sanitiseRoadmap, uid } from './schema';
import { nextColourKey } from './palette';
import { isIsoDay, mondayOf, dateToSlot, rangeDates, toIsoDay, DEFAULT_SPRINT_LENGTH } from './dates';

/* ---------- parsing and writing ---------- */

export function parseCsv(text) {
  const src = String(text || '').replace(/^﻿/, '');
  const delimiter = detectDelimiter(src);
  const rows = [];
  let row = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (quoted) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else quoted = false;
      } else field += c;
      continue;
    }
    if (c === '"' && field === '') quoted = true;
    else if (c === delimiter) {
      row.push(field);
      field = '';
    } else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else field += c;
  }
  if (field !== '' || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((f) => f.trim() !== ''));
}

function detectDelimiter(src) {
  const firstLine = src.split(/\r?\n/, 1)[0] || '';
  const counts = [',', ';', '\t'].map((d) => [d, firstLine.split(d).length]);
  counts.sort((a, b) => b[1] - a[1]);
  return counts[0][1] > 1 ? counts[0][0] : ',';
}

function cell(value) {
  const s = value === null || value === undefined ? '' : String(value);
  // Prefix cells that a spreadsheet would treat as a formula.
  const safe = /^[=+\-@]/.test(s) ? `'${s}` : s;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export function toCsv(rows) {
  return rows.map((r) => r.map(cell).join(',')).join('\r\n') + '\r\n';
}

/* ---------- sprint notation ---------- */
// Start: "3" or "S3" is the start of sprint 3, "3b" is halfway through sprint 3.
// End (inclusive): "5" or "S5" is the end of sprint 5, "5a" is halfway through sprint 5.

export function startToken(slot) {
  const n = Math.floor(slot / SLOTS_PER_SPRINT) + 1;
  return slot % 2 === 0 ? `S${n}` : `S${n}b`;
}

export function endToken(endSlot) {
  return endSlot % 2 === 0 ? `S${endSlot / 2}` : `S${(endSlot + 1) / 2}a`;
}

function parseStart(token) {
  const m = /^s?(?:print)?\s*(\d+)\s*([ab])?$/i.exec(String(token || '').trim());
  if (!m) return null;
  const n = Number(m[1]);
  if (n < 1) return null;
  return (n - 1) * 2 + (m[2]?.toLowerCase() === 'b' ? 1 : 0);
}

function parseEnd(token) {
  const m = /^s?(?:print)?\s*(\d+)\s*([ab])?$/i.exec(String(token || '').trim());
  if (!m) return null;
  const n = Number(m[1]);
  if (n < 1) return null;
  return n * 2 - (m[2]?.toLowerCase() === 'a' ? 1 : 0);
}

function parseLooseDate(value) {
  const s = String(value || '').trim();
  if (!s) return null;
  if (isIsoDay(s.slice(0, 10))) return s.slice(0, 10);
  const uk = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/.exec(s);
  if (uk) {
    const iso = `${uk[3]}-${uk[2].padStart(2, '0')}-${uk[1].padStart(2, '0')}`;
    return isIsoDay(iso) ? iso : null;
  }
  const t = Date.parse(s);
  return Number.isNaN(t) ? null : toIsoDay(new Date(t));
}

/* ---------- export ---------- */

export const CSV_COLUMNS = ['Project', 'Group', 'Status', 'People', 'Phase', 'Start sprint', 'End sprint', 'Start date', 'End date', 'Description', 'Links', 'Depends on'];

export function roadmapToCsv(roadmap) {
  const members = new Map(roadmap.members.map((m) => [m.id, m.name]));
  const phases = new Map(roadmap.phases.map((p) => [p.id, p.name]));
  const projects = new Map(roadmap.projects.map((p) => [p.id, p.name]));
  const statusLabel = (v) => STATUSES.find((s) => s.value === v)?.label || '';
  const rows = [CSV_COLUMNS];
  for (const p of roadmap.projects) {
    const common = [
      p.name,
      p.group || '',
      statusLabel(p.status),
      p.memberIds.map((id) => members.get(id)).filter(Boolean).join('; ')
    ];
    const tail = [
      p.description || '',
      (p.links || []).map((l) => l.url).join(' '),
      (p.dependsOn || []).map((id) => projects.get(id)).filter(Boolean).join('; ')
    ];
    if (!p.blocks.length) {
      rows.push([...common, '', '', '', '', '', ...tail]);
      continue;
    }
    for (const b of p.blocks) {
      const range = rangeDates(roadmap.settings, b.start, b.span);
      rows.push([
        ...common,
        phases.get(b.phaseId) || '',
        startToken(b.start),
        endToken(b.start + b.span),
        range ? toIsoDay(range.from) : '',
        range ? toIsoDay(range.to) : '',
        ...tail
      ]);
    }
  }
  return toCsv(rows);
}

/* ---------- import ---------- */

const ALIASES = {
  project: ['project', 'name', 'summary', 'title', 'initiative', 'epic name', 'project name'],
  group: ['group', 'team', 'theme', 'swimlane', 'lane', 'epic', 'area', 'stream'],
  status: ['status', 'confidence'],
  people: ['people', 'person', 'assignee', 'assignees', 'owner', 'owners', 'members', 'team members'],
  phase: ['phase', 'stage', 'type'],
  start: ['start sprint', 'start', 'from sprint', 'sprint start', 'sprint'],
  end: ['end sprint', 'end', 'to sprint', 'sprint end'],
  startDate: ['start date', 'starts', 'begin', 'target start'],
  endDate: ['end date', 'due date', 'due', 'ends', 'target end', 'finish'],
  description: ['description', 'notes', 'details'],
  links: ['links', 'link', 'url', 'urls'],
  depends: ['depends on', 'dependencies', 'blocked by']
};

function mapHeader(header) {
  const norm = header.map((h) => String(h || '').trim().toLowerCase());
  const index = {};
  for (const [field, names] of Object.entries(ALIASES)) {
    const i = norm.findIndex((h) => names.includes(h));
    if (i !== -1) index[field] = i;
  }
  return index;
}

function splitList(value) {
  const s = String(value || '').trim();
  if (!s) return [];
  return s.split(s.includes(';') ? ';' : ',').map((x) => x.trim()).filter(Boolean);
}

function parseStatus(value) {
  const s = String(value || '').trim().toLowerCase();
  if (!s) return '';
  const hit = STATUSES.find((x) => x.value === s || x.label.toLowerCase() === s);
  if (hit) return hit.value;
  if (/commit|done|progress|planned/.test(s)) return 'committed';
  if (/likely|probable|next/.test(s)) return 'likely';
  if (/explor|idea|maybe|later|backlog/.test(s)) return 'exploratory';
  return '';
}

/**
 * Build a new roadmap from CSV rows. Rows with the same project name become one project
 * with several blocks. Sprint columns win over date columns when both are present.
 */
export function csvToRoadmap(text, { name = 'Imported roadmap' } = {}) {
  const rows = parseCsv(text);
  if (rows.length < 2) throw new Error('The CSV file has no rows');
  const col = mapHeader(rows[0]);
  if (col.project === undefined) throw new Error('The CSV file needs a Project (or Name, Summary or Title) column');
  const get = (row, field) => (col[field] === undefined ? '' : String(row[col[field]] ?? '').trim());
  const body = rows.slice(1).filter((r) => get(r, 'project'));

  // Dates are only used when there are no sprint columns.
  const useDates = col.start === undefined && (col.startDate !== undefined || col.endDate !== undefined);
  let settings = { sprintCount: 6, halfSprints: false, startDate: null, sprintLengthDays: DEFAULT_SPRINT_LENGTH };
  if (useDates) {
    const starts = body.map((r) => parseLooseDate(get(r, 'startDate')) || parseLooseDate(get(r, 'endDate'))).filter(Boolean).sort();
    if (starts.length) settings.startDate = mondayOf(starts[0]);
  }

  const members = [];
  const memberByName = new Map();
  const memberId = (n) => {
    const k = n.toLowerCase();
    if (!memberByName.has(k)) {
      const m = makeMember(n);
      members.push(m);
      memberByName.set(k, m.id);
    }
    return memberByName.get(k);
  };
  const phases = [];
  const phaseByName = new Map();
  const phaseId = (n) => {
    if (!n) return null;
    const k = n.toLowerCase();
    if (!phaseByName.has(k)) {
      const p = makePhase(n, nextColourKey(phases.map((x) => x.color)));
      phases.push(p);
      phaseByName.set(k, p.id);
    }
    return phaseByName.get(k);
  };

  const projects = new Map();
  const pendingDeps = [];
  let maxSlot = 0;
  let half = false;
  for (const r of body) {
    const pname = get(r, 'project');
    const key = pname.toLowerCase();
    if (!projects.has(key)) {
      const p = makeProject({
        name: pname,
        description: get(r, 'description'),
        group: get(r, 'group'),
        status: parseStatus(get(r, 'status')),
        links: splitList(get(r, 'links').replace(/\s+/g, ';'))
      });
      projects.set(key, p);
      pendingDeps.push([p, splitList(get(r, 'depends'))]);
    }
    const p = projects.get(key);
    for (const person of splitList(get(r, 'people'))) {
      const id = memberId(person);
      if (!p.memberIds.includes(id)) p.memberIds.push(id);
    }
    let start = null;
    let end = null;
    if (!useDates) {
      start = parseStart(get(r, 'start'));
      end = col.end !== undefined ? parseEnd(get(r, 'end')) : start === null ? null : parseEnd(get(r, 'start'));
    } else if (settings.startDate) {
      const sd = parseLooseDate(get(r, 'startDate'));
      const ed = parseLooseDate(get(r, 'endDate')) || sd;
      if (sd || ed) {
        start = Math.max(0, Math.floor(dateToSlot(settings, sd || ed)));
        end = Math.max(start + 1, Math.ceil(dateToSlot(settings, ed) + 1 / (settings.sprintLengthDays / 2)));
      }
    }
    if (start !== null && end !== null && end > start) {
      if (start % 2 || end % 2) half = true;
      maxSlot = Math.max(maxSlot, end);
      p.blocks.push({ id: uid(), phaseId: phaseId(get(r, 'phase')), start, span: end - start });
    }
  }

  const byName = new Map([...projects.values()].map((p) => [p.name.toLowerCase(), p.id]));
  for (const [p, deps] of pendingDeps) {
    p.dependsOn = deps.map((d) => byName.get(d.toLowerCase())).filter((id) => id && id !== p.id);
  }

  settings = {
    ...settings,
    sprintCount: Math.min(MAX_SPRINT_COUNT, Math.max(settings.sprintCount, Math.ceil(maxSlot / SLOTS_PER_SPRINT))),
    halfSprints: half
  };
  const roadmap = createRoadmap({ name, members, phases, settings });
  return sanitiseRoadmap({ ...roadmap, projects: [...projects.values()].map((p) => ({ ...p, blocks: resolveOverlaps(p.blocks) })) });
}
